import type { OperationStoreState, LifecycleMarker } from './types'
import type { Order, OperationOpen, OperationClosed, Operation } from '../sdk'

/**
 * Selector functions for deriving data from store state.
 * All pure functions that take OperationStoreState as input.
 */

export function selectPendingOrders(state: OperationStoreState): Order[] {
  const orders: Order[] = []
  state.pendingIds.forEach((id) => {
    const op = state.operationsById.get(id)
    if (op?.state === 'order') {
      orders.push(op.order)
    }
  })
  return orders
}

export function selectOpenPositions(state: OperationStoreState): OperationOpen[] {
  const positions: OperationOpen[] = []
  state.openIds.forEach((id) => {
    const op = state.operationsById.get(id)
    if (op?.state === 'open') {
      positions.push(op.operationOpen)
    }
  })
  return positions
}

export function selectClosedPositions(state: OperationStoreState): OperationClosed[] {
  const positions: OperationClosed[] = []
  state.closedIds.forEach((id) => {
    const op = state.operationsById.get(id)
    if (op?.state === 'closed') {
      positions.push(op.operationClosed)
    }
  })
  return positions
}

export function selectPendingOrdersBySymbol(
  state: OperationStoreState,
  symbol: string
): Order[] {
  return selectPendingOrders(state).filter((o) => o.symbol === symbol)
}

export function selectOpenPositionsBySymbol(
  state: OperationStoreState,
  symbol: string
): OperationOpen[] {
  return selectOpenPositions(state).filter((p) => p.order.symbol === symbol)
}

export function selectClosedPositionsBySymbol(
  state: OperationStoreState,
  symbol: string
): OperationClosed[] {
  return selectClosedPositions(state).filter((p) => p.order.symbol === symbol)
}

export function selectOperationById(
  state: OperationStoreState,
  id: string
): Operation | undefined {
  return state.operationsById.get(id)
}

export function selectMarkers(
  state: OperationStoreState,
  symbol?: string
): LifecycleMarker[] {
  if (!symbol) {
    return state.lifecycleMarkers
  }
  return state.lifecycleMarkers.filter((m) => m.symbol === symbol)
}

export function selectHydrated(state: OperationStoreState): boolean {
  return state.hydrated
}

export function selectCurrentReplayTime(state: OperationStoreState): string | undefined {
  return state.currentReplayTime
}

export function selectCurrentReplayTimeDisplay(state: OperationStoreState): string | undefined {
  return state.currentReplayTimeDisplay
}

export function selectRemainingMatchTime(state: OperationStoreState): number | undefined {
  return state.remainingMatchTime
}
